import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import ProjectCard, { ProjectCardProps, ProjectCardSkeleton } from "./ProjectCard";
import { GameMetrics } from "../game/components/GameGallery";
import { defaultConfig } from "../../utils/config";
import { getWebsiteUrl } from "../../utils/urls";

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
    gap: 24px;
    width: 100%;
    max-width: 1400px;
    padding: 20px;
    overflow: hidden;
    
    @media (max-width: 480px) {
        grid-template-columns: repeat(auto-fit, minmax(1, 1fr));
        padding: 2px;
    }
    
    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
    
    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }
    
    @media (min-width: 1280px) {
        grid-template-columns: repeat(4, 1fr);
    }
`;

export interface Project {
    id: string;
    name: string;
    username: string;
    imageUrl?: string;
    artType?: '2D' | '3D';
    linkType?: 'game' | 'play' | 'art' | '3d' | 'animate';
    gameMetrics?: GameMetrics;
    updatedAt?: string;
}

interface ProjectGridProps {
    type: 'Game' | 'Art' | 'Animation';
    projects: Project[];
    colormode: 'light' | 'dark';
    onProjectClick?: (project: Project) => void;
    isCommunity?: boolean;
    loading?: boolean;
    pageSize?: number;
}

const ProjectGrid: React.FC<ProjectGridProps> = ({
    type,
    projects,
    colormode,
    onProjectClick,
    isCommunity,
    loading,
    pageSize = 12
}) => {
    // console.log("Current ProjectGrid type:", type);

    const renderProject = (project: Project, forceDisableClick: boolean = false, isClickableOverride?: boolean) => (
        <ProjectCard
            key={project.id}
            type={type}
            projectName={project.name}
            username={project.username}
            imageUrl={project.imageUrl}
            colormode={colormode}
            artType={project.artType}
            onClick={forceDisableClick ? undefined : () => onProjectClick?.(project)}
            isClickable={isClickableOverride}
            gameMetrics={project.gameMetrics}
            updatedAt={project.updatedAt}
            isCommunity={isCommunity}
        />
    );

    return (
        <Grid>
            {projects.map((project) => {
                const isGame = type === 'Game';
                const isAnimation = type === 'Animation';
                const shouldWrapInLink = isGame && !!project.linkType;
                
                // For Community Animations, they should not be clickable at all
                if (isAnimation && isCommunity) {
                    return renderProject(project, true, false);
                }

                if (shouldWrapInLink) {
                    // Force 'play' route for community games to bypass potential login guards
                    const finalLinkType = isCommunity ? 'play' : project.linkType;
                    const finalLink = defaultConfig.REDIRECT_TO_WEBSITE && isGame && finalLinkType === 'play' ? 
                        getWebsiteUrl(`/${finalLinkType}/${project.id}`) : 
                        `/${finalLinkType}/${project.id}`;
                    // console.log("Final link:", finalLink);
                    
                    return (
                        <Link 
                            key={project.id}
                            to={finalLink}
                            style={{ textDecoration: 'none' }}
                            target={isCommunity ? "_blank" : undefined}
                            rel={isCommunity ? "noopener noreferrer" : undefined}
                        >
                            {/* We force onClick to undefined for pop-up fix, but keep hover effect via isClickableOverride */}
                            {renderProject(project, true, true)}
                        </Link>
                    );
                }

                return renderProject(project);
            })}
            {loading && Array.from({ length: pageSize }).map((_, i) => (
                <ProjectCardSkeleton key={`skeleton-${i}`} type={type} colormode={colormode} />
            ))}
        </Grid>
    );
};

export default ProjectGrid;